"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BackButton } from "@/components/ui/back-button";
import {
  Users,
  Plus,
  Download,
  Upload,
  Search,
  Phone,
  Mail,
  Building2,
  Clock,
  Loader2,
  AlertTriangle,
  X,
  ChevronDown,
} from "lucide-react";

type Lead = {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  company: string | null;
  source: string | null;
  status: string;
  notes: string | null;
  last_contact: string | null;
  created_at: string;
};

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "contacted", label: "Contacted", color: "bg-yellow-100 text-yellow-800" },
  { value: "qualified", label: "Qualified", color: "bg-green-100 text-green-800" },
  { value: "converted", label: "Converted", color: "bg-emerald-100 text-emerald-800" },
  { value: "follow_up", label: "Follow Up", color: "bg-orange-100 text-orange-800" },
  { value: "lost", label: "Lost", color: "bg-red-100 text-red-800" },
];

function StatusBadge({ status, onClick }: { status: string; onClick?: () => void }) {
  const opt = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${opt.color}`}
    >
      {opt.label}
      {onClick && <ChevronDown className="h-3 w-3" />}
    </button>
  );
}

export default function LeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");

  // New lead form
  const [newLead, setNewLead] = useState({ name: "", phone: "", email: "", company: "", source: "manual", notes: "" });
  const [addLoading, setAddLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/leads?status=${filter}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLeads(data.leads || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const addLead = async () => {
    if (!newLead.name && !newLead.phone && !newLead.email) return;
    setAddLoading(true);
    try {
      const res = await fetch("/api/admin/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newLead),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowAdd(false);
      setNewLead({ name: "", phone: "", email: "", company: "", source: "manual", notes: "" });
      fetchLeads();
    } catch {
      // Error handled silently
    } finally {
      setAddLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    setEditingStatus(null);
    try {
      await fetch("/api/admin/leads", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status, last_contact: new Date().toISOString() }),
      });
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status, last_contact: new Date().toISOString() } : l));
    } catch { /* silent */ }
  };

  const updateNotes = async (id: string) => {
    setEditingNotes(null);
    try {
      await fetch("/api/admin/leads", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, notes: noteText }),
      });
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, notes: noteText } : l));
    } catch { /* silent */ }
  };

  const exportCSV = () => {
    window.open(`/api/admin/leads?format=csv&status=${filter}`, "_blank");
  };

  const filtered = leads.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      l.name?.toLowerCase().includes(s) ||
      l.phone?.includes(s) ||
      l.email?.toLowerCase().includes(s) ||
      l.company?.toLowerCase().includes(s)
    );
  });

  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    qualified: leads.filter((l) => l.status === "qualified").length,
    converted: leads.filter((l) => l.status === "converted").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackButton />
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Users className="h-5 w-5" /> Lead Tracker
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { label: "Total", value: stats.total },
          { label: "New", value: stats.new },
          { label: "Contacted", value: stats.contacted },
          { label: "Qualified", value: stats.qualified },
          { label: "Converted", value: stats.converted },
        ].map((s) => (
          <div key={s.label} className="text-center rounded-lg bg-muted/50 p-2">
            <p className="text-lg font-bold">{s.value}</p>
            <p className="text-[10px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-10 rounded-md border px-2 text-sm"
        >
          <option value="all">All</option>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Add lead form */}
      {showAdd && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Add Lead</CardTitle>
            <button onClick={() => setShowAdd(false)}><X className="h-4 w-4" /></button>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Name" value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} />
              <Input placeholder="Phone" type="tel" value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} />
              <Input placeholder="Email" type="email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} />
              <Input placeholder="Company" value={newLead.company} onChange={(e) => setNewLead({ ...newLead, company: e.target.value })} />
            </div>
            <Input placeholder="Notes" value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })} />
            <select
              value={newLead.source}
              onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
              className="h-10 w-full rounded-md border px-2 text-sm"
            >
              <option value="manual">Manual</option>
              <option value="website">Website</option>
              <option value="referral">Referral</option>
              <option value="cold_call">Cold Call</option>
              <option value="growth_engine">Growth Engine</option>
            </select>
            <Button onClick={addLead} disabled={addLoading} className="w-full">
              {addLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Lead"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="border-red-300">
          <CardContent className="p-4 flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4" />
            {error} — The leads table may need to be created. Run the migration in Supabase Dashboard.
          </CardContent>
        </Card>
      )}

      {/* Lead list */}
      <div className="space-y-2">
        {filtered.length === 0 && !error && (
          <p className="text-center text-sm text-muted-foreground py-8">
            No leads yet. Click &quot;Add&quot; to create your first lead.
          </p>
        )}
        {filtered.map((lead) => (
          <Card key={lead.id} className="overflow-hidden">
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{lead.name || "Unnamed"}</p>
                    <StatusBadge
                      status={lead.status}
                      onClick={() => setEditingStatus(editingStatus === lead.id ? null : lead.id)}
                    />
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                    {lead.company && (
                      <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{lead.company}</span>
                    )}
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-foreground">
                        <Phone className="h-3 w-3" />{lead.phone}
                      </a>
                    )}
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-foreground">
                        <Mail className="h-3 w-3" />{lead.email}
                      </a>
                    )}
                    {lead.last_contact && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(lead.last_contact).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status dropdown */}
              {editingStatus === lead.id && (
                <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => updateStatus(lead.id, opt.value)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${opt.color} ${lead.status === opt.value ? "ring-2 ring-offset-1 ring-primary" : ""}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Notes */}
              {lead.notes && editingNotes !== lead.id && (
                <p
                  className="text-xs text-muted-foreground mt-1.5 cursor-pointer hover:text-foreground"
                  onClick={() => { setEditingNotes(lead.id); setNoteText(lead.notes || ""); }}
                >
                  📝 {lead.notes}
                </p>
              )}
              {editingNotes === lead.id && (
                <div className="mt-2 flex gap-1.5">
                  <Input
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add notes..."
                    className="h-8 text-xs"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && updateNotes(lead.id)}
                  />
                  <Button size="sm" variant="outline" onClick={() => updateNotes(lead.id)} className="h-8 text-xs">
                    Save
                  </Button>
                </div>
              )}
              {!lead.notes && editingNotes !== lead.id && (
                <button
                  className="text-[10px] text-muted-foreground hover:text-foreground mt-1"
                  onClick={() => { setEditingNotes(lead.id); setNoteText(""); }}
                >
                  + Add notes
                </button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
